"use client";

import { StytchProvider, useStytch, useStytchSession } from "@stytch/nextjs";
import { createStytchClient } from "@stytch/nextjs/headless";
import { ConvexReactClient, ConvexProviderWithAuth } from "convex/react";
import { ReactNode, useCallback, useMemo } from "react";

// unsavedChangesWarning off: Convex's default beforeunload guard fires the
// browser "leave this page?" prompt whenever a request is in flight during a
// navigation, which happens on every OAuth redirect (Google/Discord sign-in
// leaves the page mid-request). We don't rely on the warning anywhere.
const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL!, {
  unsavedChangesWarning: false,
});

// One Stytch project per board; the deployment's board selects the public token
// (and thus the JWT issuer the backend reads as the board signal).
function stytchTokenForBoard(boardId: string): string {
  return boardId === "ESAT"
    ? process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN_ESAT!
    : process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN_TMUA!;
}

// This shim surfaces the current Stytch session JWT to Convex.
function useStytchConvexAuth() {
  const client = useStytch();
  const { session, isInitialized } = useStytchSession();
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        // Stytch session JWTs live 5 min; Convex asks for a fresh one on expiry.
        // Return the JWT the refresh mints — getTokens() can still be the stale one.
        if (forceRefreshToken) {
          const res = await client.session.authenticate({
            session_duration_minutes: 43200,
          });
          return res.session_jwt ?? null;
        }
        return client.session.getTokens()?.session_jwt ?? null;
      } catch {
        return null;
      }
    },
    [client],
  );
  return useMemo(
    () => ({
      isLoading: !isInitialized,
      isAuthenticated: !!session,
      fetchAccessToken,
    }),
    [isInitialized, session, fetchAccessToken],
  );
}

export default function ConvexClientProvider({
  boardId,
  children,
}: {
  boardId: string;
  children: ReactNode;
}) {
  const stytch = useMemo(
    () => createStytchClient(stytchTokenForBoard(boardId)),
    [boardId],
  );
  return (
    <StytchProvider stytch={stytch}>
      <ConvexProviderWithAuth client={convex} useAuth={useStytchConvexAuth}>
        {children}
      </ConvexProviderWithAuth>
    </StytchProvider>
  );
}
